import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { toHandoffIntent, type CalendarEventDto, type HandoffIntent } from '@chat-emp/shared';

import { EmptyState } from '../components/EmptyState.js';
import { ErrorBoundary } from '../components/ErrorBoundary.js';
import { Icon } from '../components/Icon.js';
import { type ComposerHandle } from '../conversation/Composer.js';
import { ConversationComposer } from '../conversation/ConversationComposer.js';
import { TemplatePanel } from '../conversation/TemplatePanel.js';
import { useTemplates } from '../conversation/useTemplates.js';
import { ConversationHeader } from '../conversation/ConversationHeader.js';
import { DeletionCountdown } from '../conversation/DeletionCountdown.js';
import {
  DetailsPanel,
  DetailsPanelError,
  type StageControl,
} from '../conversation/DetailsPanel.js';
import { EndChatActions } from '../conversation/EndChatActions.js';
import { EndChatDialog } from '../conversation/EndChatDialog.js';
import { ReportDialog } from '../conversation/ReportDialog.js';
import { HiringStepper, HiringStepperSkeleton } from '../conversation/HiringStepper.js';
import { StageBar } from '../conversation/StageBar.js';
import {
  trackHiringStage,
  type HiringSurface,
  type HiringVia,
} from '../conversation/hiringEvents.js';
import { HiringStatusPill } from '../conversation/HiringStatusPill.js';
import { useComposerDraft } from '../conversation/useComposerDraft.js';
import { MessageThread } from '../conversation/MessageThread.js';
import { AssistantCard, isRecommendedDraft } from '../conversation/AssistantCard.js';
import {
  actionForShortcut,
  CARD_ACTIONS,
  type CardAction,
} from '../conversation/assistantActions.js';
import {
  trackAssistantShown,
  trackAssistantTaken,
  trackAssistantToggled,
  trackingTranslation,
  type OpenedBy,
} from '../conversation/assistantEvents.js';
import { footerCard } from '../conversation/footerCard.js';
import {
  channelExists,
  hasChannel,
  useConversation,
  useConversationDetails,
  useConversationRow,
} from '../data/source.js';
import { stageHintFor, useChatsUnreadCount, useInfoUnread } from '../data/inbox.js';
import { Toast } from '../components/Toast.js';
import { ChatDayDialog } from '../event/ChatDayDialog.js';
import { EventCalendar } from '../event/EventCalendar.js';
import { EventDetailsPanel } from '../event/EventDetailsPanel.js';
import { EventPanel } from '../event/EventPanel.js';
import { reportCopy, useBooking, type InviteReport } from '../event/useBooking.js';
import { useChatDay } from '../event/useChatDay.js';
import { useChatDayListSync } from '../event/useChatDayListSync.js';
import { reloadEvents, useEvents } from '../event/useEvents.js';
import { blankForm, FormEditor } from '../form/FormEditor.js';
import { FormPane, type FormTab } from '../form/FormPane.js';
import { ResponsePanelHost } from '../form/ResponsePanel.js';
import { ResponseLinkContext, type ResponseLink } from '../form/responseLink.js';
import { FormsPanel } from '../form/FormsPanel.js';
import { FormTemplateDialog } from '../form/FormTemplateDialog.js';
import { useTemplateLanguage } from '../data/useTemplateLanguage.js';
import { FormTemplatesPanel } from '../form/FormTemplatesPanel.js';
import { formFromTemplate, useForms } from '../data/forms.js';
import type { Form } from '../fixtures/domain.js';
import { cidParts, emitToHost } from '../lib/bridge.js';
import { track } from '../lib/analytics.js';
import { startReplay, stopReplay } from '../lib/replay.js';
import { useConversationOpened } from '../hooks/useConversationOpened.js';
import { useHostProps } from '../hooks/useHostProps.js';
import { useAssistant } from '../hooks/useAssistant.js';
import { useQuickReplies } from '../hooks/useQuickReplies.js';
import { useHostUnread } from '../hooks/useHostUnread.js';
import { useFitsThreeColumns } from '../util/useFitsThreeColumns.js';
import { useIsMobile } from '../util/useIsMobile.js';
import { useSession } from '../session/SessionProvider.js';
import { useStreamClient } from '../session/StreamProvider.js';
import type { ClosingVariant, InboxItem, Message } from '../fixtures/domain.js';
import {
  applicationStatusOf,
  closingVariantFor,
  type HiringStage,
  firstReplyReward,
  isBroadcast,
  isJobBounded,
} from '../fixtures/rules.js';
import { BroadcastPanel } from '../inbox/BroadcastPanel.js';
import { EndedPanel } from '../inbox/EndedPanel.js';
import { InboxPanel, type FunnelContext } from '../inbox/InboxPanel.js';
import { toInboxView } from '../inbox/KindStepper.js';
import { ListsPanel } from '../inbox/ListsPanel.js';
import { AutoReplyPanel } from '../autoreply/AutoReplyPanel.js';
import { AutoReplyPreview } from '../autoreply/AutoReplyPreview.js';
import { useAutoReply } from '../autoreply/useAutoReply.js';
import { ContactsPanel } from '../contacts/ContactsPanel.js';
import { useContacts } from '../contacts/useContacts.js';
import { ChatLayout } from '../layout/ChatLayout.js';
import { ChatRail, type ChatSection } from '../layout/ChatRail.js';
import styles from './ChatView.module.css';

/**
 * URL word to rail id. `info` is the rail labelled Info, whose internal id is
 * `broadcast`; the words match chat-js, so one link means the same thing in both
 * apps and neither can rename them alone.
 *
 * A switch, not a lookup object: `TABS['constructor']` answers
 * `Object.prototype.constructor`, which a `?? 'chat'` fallback never sees.
 * Anything unrecognised is Chats, since hosts forward blind.
 */
function sectionFromTab(tab: string | null): ChatSection {
  switch (tab) {
    case 'lists':
      return 'lists';
    case 'forms':
      return 'forms';
    case 'event':
      return 'event';
    case 'autoReply':
      return 'autoReply';
    case 'ended':
      return 'ended';
    case 'info':
      return 'broadcast';
    default:
      return 'chat';
  }
}

// TODO(AI-3586): hidden until AJT's start-chat changes land; flip to restore.
const START_CHAT_ENABLED = false;

/**
 * `/chat` assembled on the fixtures data seam (`data/source`). Rail + inbox +
 * conversation + details, all reading and writing through `useInbox` /
 * `useConversation` — so the re-wiring epic swaps the seam, not this file.
 * UI-only, no backend. Everything else here (section, selection, the details
 * panel, the end-chat dialog) is view state.
 */
export function ChatView() {
  const [params] = useSearchParams();
  // Every employer has the Event section; booking a Chat Day is whitelisted only.
  const { features, limits } = useSession();
  // `?tab=` is the host's rail deep link. Seeded as the initial value rather
  // than through pickSection, which calls resetPanes and would throw away a
  // `?channel=` arriving in the same URL.
  // Seeded once, which is safe because AuthGate holds this view back until the
  // session (and with it `features`) has landed — an unresolved flag would
  // otherwise turn a whitelisted employer's deep link into Chats.
  const [section, setSection] = useState<ChatSection>(() => sectionFromTab(params.get('tab')));
  // `?channel=` is how the widget hands a conversation over when expanded to the
  // full page. Seed once; an id that names no channel just lands on the list.
  const deepLink = params.get('channel');
  const [selectedId, setSelectedId] = useState<string | null>(() =>
    deepLink && hasChannel(deepLink) ? deepLink : null,
  );
  // `?view=` is the host's deep link into a *kind* of conversation — which view
  // the inbox stepper opens on. A different axis from `?tab=`, which picks a rail
  // section: this one narrows the Chats section rather than leaving it, so the two
  // compose (`?tab=lists&view=direct` is meaningless, and simply ignored — the
  // list detail has no stepper). Read once; the employer owns the stepper after.
  const initialView = toInboxView(params.get('view'));
  // The list open in the Saved List tab. null = show the list of lists (master);
  // an id = show that list's detail view (a scoped inbox). Stays within the tab.
  const [openListId, setOpenListId] = useState<number | null>(null);
  // The Forms tab's two panes: the form being read, and the one being edited.
  // A new form has no id yet, so the editor holds the draft itself. Only the
  // library sends or attaches — a template becomes sendable by being copied.
  const formLibrary = useForms();
  const [openFormId, setOpenFormId] = useState<string | null>(null);
  const [editingForm, setEditingForm] = useState<Form | null>(null);
  // The gallery that stands in for the templates column on an empty library —
  // a moment, not a state: it closes the instant a copy is taken.
  const [templateChooserOpen, setTemplateChooserOpen] = useState(false);
  const [formEditorDirty, setFormEditorDirty] = useState(false);
  // Which tab an open form shows, and the one response open beside it in the
  // context column. The page's, so a link from a chat can land on Responses
  // with an answer open; any move between forms or sections puts both away.
  const [formTab, setFormTab] = useState<FormTab>('questions');
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [reviewHost, setReviewHost] = useState<HTMLDivElement | null>(null);
  const resetFormView = (tab: FormTab = 'questions') => {
    setFormTab(tab);
    setReviewId(null);
  };
  // The answer and the templates shelf share the context column: opening one
  // puts the other away, so neither switch is left claiming a hidden panel.
  const openReview = (id: string | null) => {
    setReviewId(id);
    if (id) setFormTemplatesOpen(false);
  };
  const toggleFormTemplates = () => {
    setFormTemplatesOpen((open) => !open);
    setReviewId(null);
  };
  // Edit means edit: a templates column beside a SAVED form being edited could
  // only offer to throw that edit away, so it hides for that case.
  const editingSaved =
    editingForm !== null && formLibrary.forms.some((form) => form.id === editingForm.id);
  const fitsThreeColumns = useFitsThreeColumns();
  // null = the section's default stands; true/false = the employer's own answer,
  // which sticks until the rail moves. Ended is read back rather than worked
  // through, so it is the one section that does not volunteer the column.
  const [detailsChoice, setDetailsChoice] = useState<boolean | null>(null);
  const detailsOpen = detailsChoice ?? (fitsThreeColumns && section !== 'ended');
  /**
   * Whether the résumé dialog is open.
   *
   * Owned here rather than inside the panel because `?open=resume` opens it on
   * arrival, and the panel cannot hear a URL. Cleared whenever the panel closes
   * or the selection moves: the dialog renders inside the panel, so a flag left
   * set would reopen the résumé the next time Info was opened — on whoever
   * happened to be selected by then.
   */
  const [resumeOpen, setResumeOpen] = useState(false);
  // Which conversation the employer has OPENED the assistant's card on, if any.
  //
  // The card arrives collapsed and is opened by hand — hence the channel that
  // opened it rather than the channel that shut it. Null, the state every
  // conversation starts in, is the collapsed one.
  //
  // Still keyed on the channel rather than stored per channel: opening the card
  // is a decision about the chat in front of you, so the next one is collapsed
  // again. That the two readings differ is the point — one reading is an
  // account of THIS conversation, and having opened the last one is not a
  // standing request to be handed every future one already open.
  const [assistantOpenFor, setAssistantOpenFor] = useState<string | null>(null);
  // Read here rather than inside the section, because both of its columns show
  // the same set: the list on the left and the grid beside it are two views of
  // one diary.
  //
  // Gated on the section being open as well as on the feature, so the diary is
  // fetched when somebody looks at it and not on every page load. Both halves
  // are needed and neither implies the other: the flag says the employer *may*
  // have a calendar, the section says they are *reading* it. Leaving the section
  // unsubscribes but does not clear the store, so coming back is free.
  const { events, loading: eventsLoading, failed: eventsFailed } = useEvents(section === 'event');
  useChatDayListSync(features.chatDay);
  // Loaded on the first visit and held here, so returning to Contacts does not refetch it.
  const [contactsSeen, setContactsSeen] = useState(section === 'contacts');
  const contacts = useContacts(features.contacts === true && contactsSeen);
  /**
   * The account's auto reply, and the draft being written against it.
   *
   * Read here rather than inside either column, because both of them show the
   * same value: the settings on the left and the preview beside it are one
   * editing session, and the draft has to be somewhere both can see it. Held by
   * the store rather than by this view for the same reason the diary is — see
   * `useAutoReply`.
   *
   * Gated on the section, so the row is read when somebody looks at it and not
   * on every page load.
   */
  const autoReply = useAutoReply(section === 'autoReply');
  /**
   * The event the details panel is showing.
   *
   * Its own state, not `selectedId`: an event is not a conversation, and the two
   * must never stand in for each other — the Event section has no channel open,
   * and every other section has no event.
   */
  const [openEventId, setOpenEventId] = useState<string | null>(null);
  /** The Chat Day being booked, if any. Null between bookings — see `useChatDay`. */
  const chatDay = useChatDay();
  /** What happened when Save was last pressed. Separate from the draft above:
   *  one is what has been typed, the other is what the server made of it. */
  const booking = useBooking();
  /**
   * A booked Chat Day whose invites did not all land. Held here rather than in
   * `useBooking` because it outlives the modal: the booking succeeded, so the
   * modal is gone, and this is the only thing left saying that three of the
   * twenty-four never heard about it.
   */
  const [inviteReport, setInviteReport] = useState<InviteReport | null>(null);
  /** A library write that failed. The editor and the pane say nothing on their
   *  own, so a refused save or delete would otherwise pass for a quiet success. */
  const [formWriteError, setFormWriteError] = useState<{
    /** Bumped per failure, and the Toast's `key`: the card owns its own clock,
     *  so a second error landing under the first would inherit a timer already
     *  running out and leave before it had been read. */
    seq: number;
    title: string;
    detail: string;
  } | null>(null);
  const isMobile = useIsMobile();
  // Read by the delete-failure restore, which runs from a promise and would
  // otherwise close over whatever the builder held when the click happened.
  const editingFormRef = useRef(editingForm);
  editingFormRef.current = editingForm;
  // The templates column: shut until asked for, switched from whichever Forms
  // screen the employer is on. Up by default it put a shelf of somebody else's
  // forms beside every form opened to read one's own — the screen where
  // templates are the least use. Starting a form goes through the gallery.
  const [formTemplatesOpen, setFormTemplatesOpen] = useState(false);
  /**
   * The open closing dialog, carrying the conversation it was opened for.
   *
   * The channel is not decoration. Confirming resolves its target through
   * `convo`, which is keyed on `selectedId` at the moment of the press — so a
   * selection that moved between opening the dialog and confirming would send
   * the rejection to whoever is selected now, under a message still naming the
   * applicant it was written for. Nothing on screen permits that today: the
   * scrim covers the row and dismisses on the way down. But that is a fact about
   * a stylesheet, and this dialog can now be opened by a link rather than a
   * deliberate press (see `handoff`), so it is worth not resting an irreversible
   * act on one. `confirmEnd` refuses when the two disagree.
   */
  const [ending, setEnding] = useState<{
    channelId: string;
    variant: ClosingVariant;
    /** Which control opened it, for `end_chat_pill_clicked` (AI-3013). The
     *  ending is reachable from six places; lumping them would answer "is the
     *  ending used" and never "which of the places we offer it gets used". */
    surface: HiringSurface;
    via: HiringVia;
  } | null>(null);
  /** Open it for whatever is selected right now. */
  const openEnd = (variant: ClosingVariant, surface: HiringSurface, via: HiringVia = 'button') => {
    if (selectedId) setEnding({ channelId: selectedId, variant, surface, via });
  };
  /**
   * `?open=` — what the widget's Details / End rows ask for when they hand a
   * chat over, the two acts a 400px panel sends here rather than performing.
   *
   * One value, carrying the channel it arrived for, because neither half can be
   * seeded straight into the pane it opens. `end` becomes a `ClosingVariant`,
   * which is a fact about the channel (job-bounded rejects, direct closes) and
   * unknowable until it hydrates. And an intent left loose is worse than late:
   * `hasChannel` says yes to any id in live mode, so a deep link to a chat that
   * never hydrates would leave the ask armed, and the next row the employer
   * clicked would open a rejection dialog on the wrong applicant.
   *
   * So it names its chat, and it is spent the moment that chat answers it.
   */
  const [handoff, setHandoff] = useState<{ channelId: string; intent: HandoffIntent } | null>(
    () => {
      const intent = toHandoffIntent(params.get('open'));
      return intent && deepLink ? { channelId: deepLink, intent } : null;
    },
  );
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [report, setReport] = useState<{ message?: Message } | null>(null);
  useEffect(() => setReport(null), [selectedId]);
  const [templateLanguage, setTemplateLanguage] = useTemplateLanguage();
  /** The message a search result asked this page to open at, with its chat. */
  const [openAt, setOpenAt] = useState<{ channelId: string; messageId: string } | null>(null);
  // A write that did not land. The optimistic overlays roll themselves back, so
  // without this the stage or the ended state just snaps back unexplained.
  const [writeError, setWriteError] = useState<string | null>(null);
  const [stageSaving, setStageSaving] = useState(false);
  const composerRef = useRef<ComposerHandle>(null);
  const templates = useTemplates();

  const convo = useConversation(selectedId);
  const understood = () => {
    convo.dismissBounce();
    composerRef.current?.focus();
  };

  // Host bridge relays (no data-seam dependency): apply host theme/context, tell
  // the host which conversation is open, and relay the live unread count.
  useHostProps();
  useConversationOpened(selectedId ?? undefined);
  // The inbox reads the read model and hydrates through this client, so it is
  // held once here rather than re-resolved per panel.
  const streamClient = useStreamClient().client;
  // The rail's two dots, neither of which watches anything. Info means an
  // announcement you have not read; Chats means an open chat holds something
  // unread — the read model owns both. The Chats number is also the host's.
  const infoUnread = useInfoUnread(streamClient);
  const chatsUnread = useChatsUnreadCount(streamClient);
  useHostUnread(chatsUnread);
  const { sessionReady, gamification, analyticsId } = useSession();

  // Analytics: the employer opened the chat app (fire once per page load).
  useEffect(() => {
    track('chat_opened');
  }, []);

  // Session replay (AI-3016). Here rather than at the app root because THIS is
  // the employer actually opening chat — the root also mounts for the widget's
  // launcher, which the overwhelming majority never open and which PostHog
  // would bill a recording for all the same. Waits for the session, since the
  // id it records under is the session's; without one it does nothing.
  useEffect(() => {
    if (!analyticsId) return;
    void startReplay(analyticsId);
    return () => stopReplay();
  }, [analyticsId]);

  // Analytics: the end-chat dialog opened (the "pill" was clicked). Keyed on
  // `ending` only — this is the dialog-open transition, not a channel change.
  // Reported against the chat the dialog names, which is the one the write will
  // land on, rather than whatever is selected when this runs.
  useEffect(() => {
    if (ending) {
      const { channelType, channelId } = cidParts(ending.channelId);
      track('end_chat_pill_clicked', {
        channel_id: channelId,
        variant: ending.variant,
        surface: ending.surface,
        via: ending.via,
      });
      emitToHost('end-chat-pill-click', { channelId, channelType, variant: ending.variant });
      // The dialog opens with its closing message already seeded. stream-chat
      // fetches an AI draft and reports `fallback` when that fetch fails; chat-emp
      // has no draft service, so it is always the fallback copy — reported
      // truthfully rather than pretending a fetch happened.
      track('end_chat_draft_fetched', { channel_id: channelId, fallback: true });
    }
  }, [ending]);

  // The hydrated row is the live one: it re-maps off `channel.updated`, so the
  // header follows phase / deleteAt / unread as they change. The clicked row is
  // only a click-time snapshot and never updates, so it is the FALLBACK — it
  // covers the frames before hydration lands (and a deep link, which has no
  // click at all, has nothing else to show). Preferring the snapshot instead
  // pinned the header to whatever was true when the row was clicked.
  const [clickedItem, setClickedItem] = useState<InboxItem | null>(null);
  const hydratedRow = useConversationRow(selectedId);
  const selected = hydratedRow ?? (clickedItem?.channel.id === selectedId ? clickedItem : null);

  const draft = useComposerDraft(
    selectedId ?? undefined,
    convo,
    selected?.counterpart.name ?? '',
    composerRef,
  );

  // Live enrichment for the open conversation. Fetched on SELECTION, not on
  // panel open: the hiring status lives in the application ES doc, and the
  // header's split button has to show the real stage from the first render.
  // Opening the panel therefore costs no extra call. Fixtures fall through to
  // the item's own data.
  const details = useConversationDetails(selected ? selected.channel.id : null);
  const profile = details.profile ?? selected?.counterpart.profile;

  // The one hiring status for this view — the header's control, the ended pill,
  // the terminal/composer gates and the panel's "Mark as" all read it. The
  // ES doc is the base; a just-clicked stage (the optimistic session override)
  // wins so everything updates instantly and rolls back together; fixtures
  // preview has no details fetch and falls back to the channel's own status.
  // 'pending'/'new' → no pill, and Shortlist offered as the move.
  const hint = selected ? stageHintFor(selected.channel.id) : undefined;
  const status =
    convo.statusOverride ??
    details.applicationStatus ??
    hint ??
    (selected ? applicationStatusOf(selected.channel) : 'pending');

  // Until the ES doc lands nothing may be pressed: the inbox hint can be stale,
  // or the read model's seeded `pending`. An override means the employer just acted.
  const statusUnknown = details.loading && !convo.statusOverride;
  // Enough to draw the controls (disabled until ES answers): the hint will do.
  const stageDrawable = !statusUnknown || hint !== undefined;

  /**
   * What the details panel can mark, and where the chat already stands.
   *
   * Only a chat with an application behind it: a direct chat has no pipeline, so
   * the panel draws no section rather than an empty one. Withheld while the
   * status is still unread for the same reason the header shows a skeleton —
   * three chips drawn at "pending" would claim a stage nobody has read.
   * `settled` on a terminal chat, where the outcome still shows but nothing
   * moves; that is the same call the header makes by showing its pill.
   *
   * The write goes through the same hook the header's control does, so both
   * re-label themselves on one pass.
   */
  const stageControl: StageControl | undefined =
    selected && isJobBounded(selected.channel) && !isBroadcast(selected.channel) && !statusUnknown
      ? {
          status,
          // The panel's advance button becomes the NEXT stage in place, so a
          // double-click would otherwise land two writes.
          busy: stageSaving,
          settled: convo.ended || status === 'rejected',
          onMark: (target) => {
            trackHiringStage({
              stage: target,
              surface: 'info_panel',
              via: 'button',
              channelId: cidParts(selected.channel.id).channelId,
            });
            setWriteError(null);
            setStageSaving(true);
            void convo
              .setApplicationStatus(target)
              .catch(() => setWriteError('Could not update the hiring stage.'))
              .finally(() => setStageSaving(false));
          },
          /* The panel asks; the conversation pane ends. `EndChatDialog` drafts
             its closing message from the thread, which only that pane holds. */
          onReject: () => openEnd('reject', 'info_panel'),
        }
      : undefined;

  // Analytics: the end-chat affordance became available on this conversation —
  // stream-chat's "pill shown", fired once per channel. chat-emp draws it as the
  // End-chat / Reject control rather than a pill, but the funnel step is the same.
  const pillShownRef = useRef<string | null>(null);
  const endChatOffered =
    selected != null && !isBroadcast(selected.channel) && !convo.ended && status !== 'rejected';
  useEffect(() => {
    if (endChatOffered && selectedId && pillShownRef.current !== selectedId) {
      pillShownRef.current = selectedId;
      track('end_chat_pill_shown', { channel_id: cidParts(selectedId).channelId });
    }
  }, [endChatOffered, selectedId]);

  // `?open=` honoured, once the chat it named is actually here — and only that
  // chat, compared on the bare id because a host may hand over the canonical
  // `chat-comp-…` where the seam holds Stream's cid (same rule as WidgetPanel).
  // Anything else on screen is a conversation the employer chose instead, and
  // the ask was never about it.
  //
  // The hiring status has to have landed too, or the dialog could open on an
  // applicant already rejected. `endChatOffered` is the same gate the control
  // itself uses: an ended, rejected or Info conversation is one to open nothing
  // on. The ask is spent either way — a link that arrives on a chat with nothing
  // to end has still been answered.
  useEffect(() => {
    if (!handoff || !selected || statusUnknown) return;
    if (cidParts(selected.channel.id).channelId !== cidParts(handoff.channelId).channelId) return;
    setHandoff(null);
    if (handoff.intent === 'details') {
      // Straight to the state, not through `toggleDetails`: arriving on an open
      // panel is navigation, not a tab switch (see the note there).
      setDetailsChoice(true);
      return;
    }
    if (handoff.intent === 'resume') {
      // The panel AND the file. A link that opened the panel and left the
      // employer to hunt for the résumé chip would not have answered the button
      // they pressed, which said View Resume.
      setDetailsChoice(true);
      setResumeOpen(true);
      return;
    }
    if (!endChatOffered) return;
    // Named against `selected`, not `handoff.channelId`: the two agree by the
    // check above, and this is the id the write will resolve through.
    setEnding({
      channelId: selected.channel.id,
      variant: isJobBounded(selected.channel) ? 'reject' : 'close',
      // The widget pressed it; /chat only opens the dialog. Which of the
      // widget's two controls carried it does not survive `?open=` — see
      // `HiringVia`.
      surface: 'widget',
      via: 'handoff',
    });
  }, [handoff, selected, statusUnknown, endChatOffered]);

  // Analytics: the 30-minute deletion countdown began — the channel moved
  // `closing` → `pending_delete`. Purely client-side, as in stream-chat: the seam
  // already re-maps `phase` off live `channel.updated` events. The previous phase
  // is tracked per channel so switching INTO an already-counting-down chat reads
  // as navigation, not as a transition.
  const phase = selected?.channel.phase ?? null;
  const prevPhaseRef = useRef<{ id: string | null; phase: string | null }>({
    id: null,
    phase: null,
  });
  useEffect(() => {
    const prev = prevPhaseRef.current;
    if (
      prev.id === selectedId &&
      phase === 'pending_delete' &&
      prev.phase !== 'pending_delete' &&
      selectedId
    ) {
      track('end_chat_countdown_started', { channel_id: cidParts(selectedId).channelId });
    }
    prevPhaseRef.current = { id: selectedId, phase };
  }, [phase, selectedId]);

  // Select a channel (user clicked a row): mirror stream-chat's `channel-select`
  // host event, then set the selection. Deep-link/back go through setSelectedId
  // directly — those aren't a row click.
  const selectChannel = (item: InboxItem, funnel?: FunnelContext, messageId?: string): void => {
    const id = item.channel.id;
    // Where in the thread to open, when the row pressed was a search result
    // naming one message. Set on every select, null included, so a chat opened
    // the ordinary way does not inherit the last landing and jump into its own
    // history. The channel rides along so the landing can be checked against
    // the conversation actually on screen.
    setOpenAt(messageId ? { channelId: id, messageId } : null);
    setClickedItem(item);
    {
      const { channelType, channelId } = cidParts(item.channel.id);
      emitToHost('channel-select', {
        id: channelId,
        type: channelType,
        name: item.counterpart?.name ?? '',
        memberCount: item.channel.memberIds?.length ?? 0,
        // Stream's tally, not the read model's 0/1 — this payload is documented
        // as a count (docs/HOST_INTEGRATION.md) in parity with the
        // `@ajt/stream-chat` component a host may still have a handler for.
        unread: item.channel.unreadMessages ?? item.channel.unreadCount,
      });
      // The funnel rides along so an open can be attributed to the filter that
      // produced it (AI-2858) and to the search (AI-3011). Absent off the inbox
      // — the Ended and Info rails have neither — and `false` / `chats` are then
      // the honest answers: no search bar to have text in, and one list.
      track('channel_selected', {
        channel_id: channelId,
        channel_type: channelType,
        filter_active: funnel?.filter_active ?? false,
        search_active: funnel?.search_active ?? false,
        result_list: funnel?.result_list ?? 'chats',
        // Sent whole or not at all. Dropping just `results_count` when it is
        // unknown makes a missing key mean two different things — opened off a
        // rail with no funnel, or opened while the count was in flight — and the
        // second is the filter -> open step this funnel is built to see.
        ...(funnel
          ? {
              facets_applied: funnel.facets_applied,
              values_count: funnel.values_count,
              results_count: funnel.results_count,
            }
          : {}),
      });
    }
    setSelectedId(id);
  };

  const backToList = (): void => {
    if (selected) {
      const { channelType, channelId } = cidParts(selected.channel.id);
      emitToHost('back-to-channel-list', {
        channelId,
        channelType,
        channelName: selected.counterpart?.name ?? '',
      });
    }
    setSelectedId(null);
  };

  // `channel-list-loaded` moved to InboxPanel: it is the inbox's event, and the
  // inbox is the only thing here that still holds a list of channels.

  // Suggestions answer the applicant, so they only belong under a message that
  // is not ours. With our own message last there is nothing to reply to, and an
  // empty chat has no transcript for the BFF to suggest from — hide both.
  const lastMessage = convo.messages[convo.messages.length - 1];
  const awaitingReply = lastMessage != null && !convo.isOwnMessage(lastMessage);

  // A conversation either card may appear on. Gated on sessionReady so the
  // fixtures preview never hits the BFF (which would 401 → hang on reauth).
  // Mirrors the header's `terminal` gate (ended OR rejected) so a rejected chat
  // makes no AI calls, matching the footer that no longer renders them. Waits
  // for the ES status too: spending an AI call on a chat that turns out to be
  // rejected is the same waste, just bought a few hundred ms earlier.
  //
  // Info threads are excluded outright: a marketing broadcast has nobody to
  // advise about and no application behind it.
  //
  // Note what is NOT here: whether the applicant spoke last. That is the quick
  // replies' rule, applied in `footerCard` to that card alone — see the note
  // there on why the assistant does not inherit it.
  const liveConversation =
    sessionReady &&
    selected != null &&
    !convo.ended &&
    !statusUnknown &&
    status !== 'rejected' &&
    !isBroadcast(selected.channel);

  // The assistant card, when the backend says it is on for this employer. Its
  // switch answers once per session; until it does, neither card is drawn.
  const assistant = useAssistant(selectedId, convo.messages, liveConversation);

  // Whether the footer card is open, and the one way to say so.
  //
  // Named here rather than spelled out at each prop because it is now told to
  // more than the card: the account inside it is cut to two lines until the same
  // state says otherwise, and its "Read more" is that state's second switch.
  const assistantOpen = assistantOpenFor === selectedId;
  const openAssistant = (open: boolean) => setAssistantOpenFor(open ? selectedId : null);
  const toggleAssistant = (open: boolean, by?: OpenedBy) => {
    const { reading, suggest } = assistant;
    trackAssistantToggled({ open, reading, by, suggest, status, surface: 'chat' });
    openAssistant(open);
  };

  // One card, and therefore one lane. `footerCard` owns that decision so it can
  // be read and tested on its own; both hooks and the footer below take their cue
  // from it, which is what keeps two AI lanes from ever running at once.
  const card = footerCard(liveConversation, awaitingReply, assistant.enabled, assistant.silent);

  // The legacy LANE — the AI-written quick replies — runs only where the switch
  // says the assistant is off for this employer. The same card now also stands in
  // for an assistant that had nothing to show, and that one takes the static
  // openers: one conversation never spends two AI lanes, and a card standing in
  // for a dead request must not spend a second one just as likely to die.
  const legacyLane = card === 'legacy' && assistant.enabled === false;

  // One report per reading that actually reached the employer.
  //
  // Keyed on the channel the reading was DRAWN FROM, not the one now open: those
  // differ for a render on every conversation switch, and keying on the open one
  // counted a bogus row each time — pairing channel B's id with channel A's
  // account, in the very metric this rollout is judged by.
  const shownKey =
    card === 'assistant' && !assistant.reading && assistant.readingFor === selectedId
      ? `${assistant.readingFor}:${assistant.intro}`
      : null;
  const shownRef = useRef<string | null>(null);
  useEffect(() => {
    if (!shownKey || shownRef.current === shownKey) return;
    shownRef.current = shownKey;
    trackAssistantShown({ suggest: assistant.suggest, status, surface: 'chat' });
  }, [shownKey, assistant.suggest, status]);

  const quickReplies = useQuickReplies(selectedId, convo.messages, convo.isOwnMessage, legacyLane);

  /**
   * A user-initiated details toggle. chat-emp has no messages/info tabs; the
   * details panel is that same switch — conversation vs candidate info — so it
   * carries stream-chat's `channel_tab_switched`. Programmatic changes (a rail
   * move via resetPanes, which drops the choice) deliberately do NOT go through
   * here: they are navigation, not a tab switch, and would inflate the count.
   */
  function toggleDetails(open: boolean) {
    setDetailsChoice(open);
    if (!open) setResumeOpen(false);
    track('channel_tab_switched', { tab: open ? 'info' : 'messages' });
  }

  function resetPanes() {
    setSelectedId(null);
    setDetailsChoice(null);
    setResumeOpen(false);
    setOpenEventId(null);
    setEnding(null);
    // A handoff that never resolved dies with the selection it was about.
    setHandoff(null);
  }

  function pickSection(next: ChatSection) {
    // A rail click resets the Saved List tab to its master (the list of lists).
    setOpenListId(null);
    setOpenFormId(null);
    setEditingForm(null);
    setTemplateChooserOpen(false);
    resetFormView();
    setSection(next);
    if (next === 'contacts') setContactsSeen(true);
    resetPanes();
  }

  /** The "View list" trip a filing toast offers: switch to the Saved List tab
   *  and open that list's detail view. */
  /** A copy in the builder, still unwritten: fresh ids, and Save is the only
   *  write. Its own function rather than an argument to the "+" handler, which
   *  is handed straight to an onClick. */
  function openFormFromTemplate(template: Form) {
    setOpenFormId(null);
    resetFormView();
    setEditingForm(formFromTemplate(template));
    setTemplateChooserOpen(false);
  }

  /** A form's page in Forms, from a card or a Details row in a chat. */
  function showForm(formId: string, tab: FormTab, responseId: string | null = null) {
    pickSection('forms');
    setOpenFormId(formId);
    setFormTab(tab);
    openReview(responseId);
  }

  /** A chat started from Contacts: Chats, with that conversation open. */
  function openContactChat(channelId: string) {
    pickSection('chat');
    setSelectedId(channelId);
  }

  function openSavedList(listId: number) {
    setSection('lists');
    setOpenListId(listId);
  }

  /** The host owns the start-chat picker (it wraps the flow in the credits it
   *  awards). We only announce the intent; if the host ignores it, nothing
   *  happens here — the correct failure mode for a delegated action. */
  function startChat() {
    emitToHost('start-chat-click');
    track('start_chat_job_clicked');
  }
  const onStartChat = START_CHAT_ENABLED ? startChat : undefined;

  function confirmEnd(text: string) {
    if (!ending) return;
    // The selection moved out from under an open dialog. `convo` is keyed on
    // `selectedId`, so going ahead would end the wrong chat — and for `reject`,
    // reject the wrong jobseeker — under a closing message written for someone
    // else. Nothing on screen allows this today; it refuses rather than rely on
    // that staying true.
    //
    // Compared on the bare id, the same rule the handoff effect above uses and
    // for the same reason: `ending.channelId` comes from `selected.channel.id`,
    // which `mapChannel` always fills from Stream's cid, while `selectedId` is
    // whatever `?channel=` carried and is never normalised. A host that deep
    // links the canonical `chat-comp-…` it holds leaves the two in different
    // forms for one real chat, and a strict compare would read that as a moved
    // selection — refusing an End & reject the employer believes landed.
    if (cidParts(ending.channelId).channelId !== cidParts(selectedId ?? '').channelId) {
      setEnding(null);
      return;
    }
    const failure =
      ending.variant === 'reject' ? 'Could not reject the applicant.' : 'Could not end the chat.';
    setWriteError(null);
    void convo.endWithMessage(text, ending.variant).catch(() => setWriteError(failure));
    setEnding(null);
  }

  const openEvent = events.find((event) => event.id === openEventId) ?? null;

  const calendar = (
    <EventCalendar
      events={events}
      openedId={openEventId}
      onOpen={(event: CalendarEventDto) => setOpenEventId(event.id)}
      // The failure from a previous booking is dropped on the way in, not left
      // for the new draft to open underneath: it is about a Chat Day that is no
      // longer on screen, and there is nothing on the fresh form it refers to.
      onNewChatDay={
        features.booking
          ? (start) => {
              booking.reset();
              chatDay.open(start);
            }
          : undefined
      }
    />
  );

  // A form card or Details row in a chat goes to the form's page while the
  // form is still in the library; a deleted one keeps its answers in place.
  const responseLink: ResponseLink = {
    opens: (formId) => formLibrary.forms.some((form) => form.id === formId),
    open: (formId, responseId) => showForm(formId, 'responses', responseId),
    openForm: (formId) => showForm(formId, 'questions'),
  };

  return (
    <ResponseLinkContext.Provider value={responseLink}>
      <ChatLayout
        hasSelection={!!selected}
        rail={
          <ChatRail
            section={section}
            onSection={pickSection}
            onStartChat={onStartChat}
            dots={{ chat: (chatsUnread ?? 0) > 0, broadcast: infoUnread }}
            hidden={{ contacts: !features.contacts }}
          />
        }
        inbox={renderInbox()}
        conversation={renderConversation()}
        contextPanel={
          // One column, three tenants, and they can never want it at once: the
          // details panel belongs to an open conversation, an open event only
          // exists in a section that has none, and the templates shelf belongs
          // to Forms — hidden while a SAVED form is being edited, since a
          // template beside an edit could only offer to throw the edit away.
          section === 'forms' && reviewId && !editingForm && formTab === 'responses' ? (
            <ResponsePanelHost onMount={setReviewHost} />
          ) : section === 'forms' && formTemplatesOpen && !editingSaved ? (
            <FormTemplatesPanel
              templates={formLibrary.templates}
              loading={formLibrary.loading}
              error={formLibrary.error}
              language={templateLanguage}
              onLanguage={setTemplateLanguage}
              guarded={editingForm !== null && formEditorDirty}
              onPick={openFormFromTemplate}
              onClose={() => setFormTemplatesOpen(false)}
            />
          ) : openEvent ? (
            <EventDetailsPanel
              event={openEvent}
              onClose={() => setOpenEventId(null)}
              onEdit={(event) => {
                booking.reset();
                chatDay.openEdit(event);
              }}
            />
          ) : // Never on an Info thread: the header offers no way in (see
          // `onToggleDetails` below), and this is the other door — a host
          // handoff carrying a details intent, or a panel left open when the
          // employer crossed from a conversation to an announcement.
          detailsOpen && selected && !isBroadcast(selected.channel) ? (
            // A crash rendering unvalidated candidate data must not blank the page.
            // Keyed so a crash in one conversation does not stick to the next.
            <ErrorBoundary
              key={selected.channel.id}
              where="details-panel"
              fallback={<DetailsPanelError onClose={() => toggleDetails(false)} />}
            >
              <DetailsPanel
                key={selected.channel.id}
                onReport={() => setReport({})}
                loading={details.loading}
                failed={details.failed}
                onRetryDetails={details.refetch}
                messages={convo?.messages ?? []}
                item={{
                  ...selected,
                  counterpart: {
                    ...selected.counterpart,
                    profile,
                  },
                  channel: {
                    ...selected.channel,
                    // Merged, not replaced: the channel names the job (id,
                    // title, location) and the details route fills in the
                    // posting the channel does not mirror (type, description,
                    // responsibilities, requirements).
                    ...(selected.channel.jobMeta
                      ? { jobMeta: { ...selected.channel.jobMeta, ...details.jobMeta } }
                      : {}),
                    ...(details.appliedAt ? { createdAt: details.appliedAt } : {}),
                    applicationStatus: status,
                  },
                }}
                onClose={() => toggleDetails(false)}
                resumeOpen={resumeOpen}
                onResumeOpenChange={setResumeOpen}
                stage={stageControl}
              />
            </ErrorBoundary>
          ) : undefined
        }
      />
      {/* Over the whole page rather than inside the calendar column: it is a
          modal, and the calendar is what it is covering. */}
      <ChatDayDialog
        editor={chatDay}
        client={streamClient}
        saving={booking.saving}
        error={booking.error}
        // Only a booking that landed closes this. A failed one leaves the modal
        // up with the draft it was written in, because closing first would mean
        // the employer finds out from an empty calendar, later, if at all.
        onSave={(draft, note) => {
          const editing = chatDay.state?.editing;
          const saved = editing
            ? booking.addGuests(editing, draft.invitees, note)
            : booking.book(draft, note);
          void saved.then((report) => {
            if (!report) return;
            chatDay.close();
            // Only where the two numbers disagree. "24 of 24 invited" is a card
            // reporting that the normal thing happened, in front of a calendar
            // that already shows the Chat Day it is talking about.
            if (report.sent < report.total) setInviteReport(report);
          });
        }}
      />
      {/* The starting points, over the section that asked for them. Closes on
          the press: what comes back is the builder holding the copy. */}
      {templateChooserOpen && (
        <FormTemplateDialog
          templates={formLibrary.templates}
          loading={formLibrary.loading}
          error={formLibrary.error}
          language={templateLanguage}
          onLanguage={setTemplateLanguage}
          onClose={() => setTemplateChooserOpen(false)}
          onBlank={() => {
            setTemplateChooserOpen(false);
            setOpenFormId(null);
            setEditingForm(blankForm());
          }}
          onUse={openFormFromTemplate}
        />
      )}
      {/* One slot, stacked: two of these fixed to the same corner sat on top of
          each other, and the newer card simply hid the older one. */}
      {(inviteReport || formWriteError) && (
        <div className={styles.toastSlot}>
          {inviteReport && (
            <Toast {...reportCopy(inviteReport)} onClose={() => setInviteReport(null)} />
          )}
          {formWriteError && (
            <Toast
              key={formWriteError.seq}
              title={formWriteError.title}
              detail={formWriteError.detail}
              onClose={() => setFormWriteError(null)}
            />
          )}
        </div>
      )}
    </ResponseLinkContext.Provider>
  );

  function renderInbox() {
    switch (section) {
      case 'lists':
        // Master (the list of lists) until one is opened, then that list's
        // detail view — a scoped inbox with a back header — inside this tab.
        return openListId ? (
          // Keyed apart from the Chats inbox below: both are <InboxPanel> in the
          // same layout slot, so without distinct keys React reconciles the one
          // instance across a detail→Chats switch and its mount-seeded state
          // (scope, selection, search) leaks into the main inbox.
          <InboxPanel
            key={`list-${openListId}`}
            client={streamClient}
            selectedId={selectedId}
            initialListId={openListId}
            onExitList={() => setOpenListId(null)}
            onSelect={selectChannel}
            onNewChat={onStartChat}
            onOpenList={openSavedList}
          />
        ) : (
          <ListsPanel client={streamClient} onOpen={setOpenListId} />
        );
      case 'event':
        // A phone has one column, and for this section it should be the
        // calendar: the list beside it is a list *of* what the calendar is
        // showing, and the grid is the part that cannot be read anywhere else.
        return isMobile ? (
          calendar
        ) : (
          <EventPanel
            events={events}
            loading={eventsLoading}
            failed={eventsFailed}
            onRetry={reloadEvents}
            selectedId={openEventId}
            onOpen={(event) => setOpenEventId(event.id)}
          />
        );
      case 'forms':
        return (
          <FormsPanel
            forms={formLibrary.forms}
            openFormId={editingForm ? editingForm.id : openFormId}
            loading={formLibrary.loading}
            error={formLibrary.error}
            onRetry={formLibrary.reload}
            onOpen={(id) => {
              setOpenFormId(id);
              setEditingForm(null);
              resetFormView();
            }}
            // Start a form: the gallery, whichever press asked. The "+" used
            // to open an empty builder while a word under it went to the
            // templates — ranking a blank page against forms nobody had seen.
            // The gallery asks that once, blank card first.
            onNew={() => setTemplateChooserOpen(true)}
          />
        );
      case 'autoReply':
        // Null until the first read lands, so the switch is never drawn in a
        // state nobody has confirmed — an employer whose auto reply is on must
        // not see it flash off.
        return autoReply.draft ? (
          <AutoReplyPanel
            draft={autoReply.draft}
            forms={formLibrary.forms}
            formsPending={formLibrary.loading || formLibrary.error}
            dirty={autoReply.dirty}
            saving={autoReply.saving}
            onChange={autoReply.setDraft}
            onSave={() => void autoReply.save()}
            // The Forms tab is where one is written — it exists now.
            // To the library, not into a blank builder: every new form starts
            // at the gallery, and the library's "+" is where it opens.
            onWriteForm={() => pickSection('forms')}
          />
        ) : null;
      case 'contacts':
        return <ContactsPanel book={contacts} onStarted={openContactChat} />;
      case 'ended':
        return (
          <EndedPanel client={streamClient} selectedId={selectedId} onSelect={selectChannel} />
        );
      case 'broadcast':
        return (
          <BroadcastPanel client={streamClient} selectedId={selectedId} onSelect={selectChannel} />
        );
      default:
        return (
          <InboxPanel
            key="inbox"
            client={streamClient}
            selectedId={selectedId}
            initialView={initialView ?? undefined}
            onSelect={selectChannel}
            onNewChat={onStartChat}
            onOpenList={openSavedList}
          />
        );
    }
  }

  function renderConversation() {
    // The Event section owns this column outright: it holds no conversations, so
    // there is no "nothing selected" state to fall to — the calendar is the
    // pane. Mobile renders it in the column above instead (see renderInbox),
    // where it would otherwise be unreachable.
    if (section === 'event') return isMobile ? null : calendar;

    // The Forms section owns it the same way: the builder, an open form, or
    // the invitation to pick one.
    if (section === 'forms') {
      if (editingForm) {
        return (
          <FormEditor
            key={editingForm.id}
            initial={editingForm}
            saving={formLibrary.saving}
            maxFields={limits?.formMaxFields}
            templatesOpen={formTemplatesOpen}
            onToggleTemplates={toggleFormTemplates}
            onDirtyChange={setFormEditorDirty}
            onSave={(form) => {
              void formLibrary
                .save(form)
                .then((saved) => {
                  setEditingForm(null);
                  setOpenFormId(saved.id);
                  resetFormView();
                })
                .catch(() =>
                  setFormWriteError((prev) => ({
                    seq: (prev?.seq ?? 0) + 1,
                    title: 'That form was not saved',
                    detail: 'Nothing was lost — the questions are still here. Try Save again.',
                  })),
                );
            }}
            onCancel={() => setEditingForm(null)}
          />
        );
      }
      const openForm = formLibrary.forms.find((form) => form.id === openFormId);
      if (openForm) {
        return (
          <FormPane
            form={openForm}
            templatesOpen={formTemplatesOpen}
            onToggleTemplates={toggleFormTemplates}
            tab={formTab}
            onTab={resetFormView}
            reviewId={reviewId}
            onReview={openReview}
            reviewHost={reviewHost}
            onOpenChat={openContactChat}
            onChatExists={channelExists}
            onEdit={() => setEditingForm(openForm)}
            onDelete={() => {
              const { id } = openForm;
              setOpenFormId(null);
              resetFormView();
              void formLibrary.remove(id).catch(() => {
                // Put the pane back: the row never left the list, and a closed
                // pane beside it is the app saying the delete worked. Only if
                // nothing has claimed the column since — another form, or the
                // builder, which holds it with `openFormId` at null and would
                // otherwise uncover the deleted form on Cancel.
                if (editingFormRef.current === null) {
                  setOpenFormId((current) => current ?? id);
                }
                setFormWriteError((prev) => ({
                  seq: (prev?.seq ?? 0) + 1,
                  title: 'That form is still here',
                  detail: 'Deleting it did not go through. Try again.',
                }));
              });
            }}
          />
        );
      }
      return (
        <div className={styles.placeholder}>
          <EmptyState
            icon={<Icon name="listChecks" size={32} />}
            title="Select a form"
            description="Open one to read its questions. Answers come back in the chat you sent it in."
          />
        </div>
      );
    }

    // The Auto reply section owns this column too, and holds no conversations.
    // Inverted from Event on a phone: there the grid is the half that cannot be
    // read anywhere else, and here it is the SETTINGS — a preview is the half
    // you cannot act on, so a single column spends itself on the form.
    if (section === 'autoReply')
      return isMobile || !autoReply.draft ? null : (
        <AutoReplyPreview
          reply={autoReply.draft}
          form={formLibrary.forms.find((f) => f.id === autoReply.draft?.formId) ?? null}
        />
      );

    if (!selected)
      return (
        <div className={styles.placeholder}>
          <EmptyState
            title="Select a conversation"
            description="Pick one from the list to read it and reply."
          />
        </div>
      );

    const channel = selected.channel;
    const broadcast = isBroadcast(channel);
    const jobBounded = isJobBounded(channel);
    const ended = convo.ended;
    // A rejected applicant is terminal even before the channel closes server-side
    // (that write is deferred): the header shows the read-only pill and the
    // composer locks, so nothing ever offers to re-shortlist a reject.
    const terminal = ended || status === 'rejected';
    const variant = closingVariantFor(jobBounded);

    /**
     * The keys the card prints, wired to the rows they name.
     *
     * ⇧1–4 are the actions in their fixed order and ⇧5 onward the drafts, so a
     * key means the same thing in every conversation. A key whose row is not on
     * screen — a stage on a direct chat, a draft the model did not write — does
     * nothing rather than guessing at a neighbour.
     */
    const takeAssistantKey = (digit: number) => {
      if (digit <= CARD_ACTIONS.length) {
        // Asked of the card's current state, not of the numbering: the keys are
        // fixed but the rows on screen are not, and a key naming a row nobody
        // can see must do nothing — for the ending, emphatically so.
        const action = actionForShortcut(digit, jobBounded, status);
        if (action) takeAssistantAction(action, 'shortcut');
        return;
      }
      // No longer gated on the reading having recommended a message. The drafts
      // sit under the send row on every reading now, so whether a key answers is
      // a question about whether a draft is there — which the lookup below already
      // asks.
      const draft = assistant.replies[digit - CARD_ACTIONS.length - 1];
      if (draft === undefined) return;
      const index = digit - CARD_ACTIONS.length - 1;
      assistant.select('reply', String(index));
      trackAssistantTaken({
        draftIndex: index,
        recommended: isRecommendedDraft(assistant.recommended, index, assistant.replies.length),
        by: 'shortcut',
        surface: 'chat',
      });
      composerRef.current?.insert(draft);
    };

    /**
     * Taking a row the assistant offered.
     *
     * Reported before the act, so a move that fails still records that the
     * employer agreed with it. Both card sections share this: a recommended row
     * and the same row in the fallback list must do the same thing.
     */
    const takeAssistantAction = (action: CardAction, by: 'press' | 'shortcut' = 'press') => {
      assistant.select('action', action);
      trackAssistantTaken({
        action,
        recommended: assistant.suggest === action,
        by,
        surface: 'chat',
      });
      if (action === 'end') {
        // The same dialog the header's End control opens: it writes the closing
        // message, and nothing ends until the employer confirms there.
        openEnd(variant, 'assistant');
        return;
      }
      // The same mutation the header's split button performs — no second source
      // of truth for what a stage move does.
      setWriteError(null);
      void convo
        .setApplicationStatus(action)
        .catch(() => setWriteError('Could not update the hiring stage.'));
    };
    // The rule owns every gate (job-bounded, budget left, reply still owed); the
    // host's config is null until it sends one, which reads as "no nudge".
    const reward = firstReplyReward(selected.channel, gamification);

    const markStage = (stage: HiringStage) => {
      trackHiringStage({
        stage,
        surface: 'strip',
        via: 'button',
        channelId: cidParts(channel.id).channelId,
      });
      setWriteError(null);
      setStageSaving(true);
      void convo
        .setApplicationStatus(stage)
        .catch(() => setWriteError('Could not update the hiring stage.'))
        .finally(() => setStageSaving(false));
    };

    const actions = broadcast ? undefined : (
      <>
        {/* The seam already folds the ended overlay into the channel (phase
            'closing'), so the countdown reads the trash/ended treatment directly. */}
        <DeletionCountdown
          channel={channel}
          counterpartName={selected.counterpart.name}
          variant={variant}
          onEnd={(v) => openEnd(v, 'countdown')}
        />
        {/*
          NOTHING OF THE PIPELINE UP HERE.

          The stages and the ending are a strip under this header, where all of
          them are on screen at once (`HiringStepper`). A header has room for one
          control, and one control could only ever be the next move — which is
          what the redesign was asked to stop being the whole story. What is left
          is the countdown beside this, the details toggle the header draws
          itself, and, once a chat has ended, its outcome.

          A direct chat is untouched by all of it: no pipeline, so no strip, and
          `EndChatActions` here is still the only way to close one.
        */}
        {terminal ? (
          /* A pill six inches from a pill is one pill too many: the panel's
             Outcome section states this same fact, so the header's copy stands
             down while it is open. Conditioned on `stageControl` rather than on
             `jobBounded`, because that is the thing drawing the other pill — a
             chat the panel draws no outcome for keeps the header's. */
          stageControl && detailsOpen ? null : (
            <HiringStatusPill status={status} />
          )
        ) : jobBounded ? null : (
          <span className={styles.wideOnly}>
            <EndChatActions
              onEnd={() => openEnd('close', 'header')}
              onDetails={() => toggleDetails(true)}
            />
          </span>
        )}
      </>
    );

    return (
      <div className={styles.convo} key={selected.channel.id}>
        <ConversationHeader
          counterpart={broadcast ? selected.counterpart : { ...selected.counterpart, profile }}
          jobTitle={selected.channel.jobMeta?.jobTitle}
          byEmail={selected.channel.byEmail}
          appliedAt={selected.channel.jobMeta ? details.appliedAt : undefined}
          actions={actions}
          onBack={backToList}
          stage={jobBounded && !terminal && stageDrawable ? status : undefined}
          // Undefined on a broadcast: nothing to open, so nothing offers to.
          onToggleDetails={
            broadcast
              ? undefined
              : () => {
                  // stream-chat parity: pressing the header identity ==
                  // avatar-click, whichever way it moves the panel. Gating this
                  // on the opening press lost it once the panel began arriving
                  // open, leaving the host's only details signal on the rare half.
                  if (selected) {
                    const { channelType, channelId } = cidParts(selected.channel.id);
                    emitToHost('channel-header-avatar-click', {
                      id: channelId,
                      type: channelType,
                    });
                  }
                  toggleDetails(!detailsOpen);
                }
          }
          detailsOpen={detailsOpen}
          profilePending={!broadcast && (details.loading || details.failed)}
        />
        {/*
          WHERE THIS APPLICANT STANDS, UNDER THE HEADER.

          Fixed, with the thread scrolling beneath it, so the whole pipeline
          stays on screen the length of the conversation. A pipeline chat that is
          still open: an ended one has an outcome rather than a stage and reports
          it as a pill above, and a direct chat has no pipeline at all.

          Skeletoned until the stage is read, for the reason the header's control
          was: drawing three steps with none of them marked would claim a
          "pending" nobody has actually read off the ES doc.
        */}
        {jobBounded &&
          !terminal &&
          (!stageDrawable ? (
            <HiringStepperSkeleton />
          ) : (
            <>
              {/* Both mounted; the column's width shows one. */}
              <HiringStepper
                status={status}
                busy={statusUnknown}
                onStage={markStage}
                onReject={() => openEnd('reject', 'strip')}
              />
              <StageBar
                status={status}
                busy={stageSaving || statusUnknown}
                onStage={markStage}
                onReject={() => openEnd('reject', 'strip')}
              />
            </>
          ))}
        {!jobBounded && !terminal && !broadcast && (
          <StageBar
            status={status}
            jobBounded={false}
            onStage={markStage}
            onReject={() => openEnd('close', 'strip')}
          />
        )}
        {writeError && (
          <p className={styles.writeError} role="alert">
            {writeError} Please try again.
          </p>
        )}
        <MessageThread
          messages={convo.messages}
          loading={false}
          isOwnMessage={convo.isOwnMessage}
          currentUserId={convo.currentUserId}
          sendReply={(text, id, plain) =>
            convo.send(text, id, undefined, plain).then(() => undefined)
          }
          counterpartName={selected.counterpart.name}
          // An ended chat is read-only, so it offers no way to start a reply or an edit.
          onReplyTo={terminal ? undefined : draft.startReply}
          onEdit={terminal ? undefined : draft.startEdit}
          bounce={convo.bounce && { ...convo.bounce, surface: 'chat', dismiss: understood }}
          onReport={broadcast ? undefined : (message) => setReport({ message })}
          focusMessageId={openAt?.channelId === selected.channel.id ? openAt.messageId : null}
          className={styles.thread!}
          footer={
            /*
             * One card, chosen once the assistant's switch is known.
             *
             * Unknown draws nothing. Rendering the legacy card while waiting
             * would change the footer's design under the employer a second
             * later, and its loading state would be a claim about a lane that is
             * not the one running — a card that arrives late is better than one
             * that arrives wrong.
             *
             * Reverting this expression, leaving the new files in place, returns
             * the footer to quick replies exactly as it was.
             */
            card !== 'none' ? (
              card === 'assistant' ? (
                <AssistantCard
                  expanded={assistantOpen}
                  onExpandedChange={toggleAssistant}
                  // Nothing to press while a reading is in flight: the rows the
                  // keys name belong to the reading that has just been withdrawn.
                  onShortcut={assistant.reading ? undefined : takeAssistantKey}
                  reading={assistant.reading}
                  suggest={assistant.reading ? null : assistant.suggest}
                  /* The verdict this reading came back with. Employer-scoped,
                     because that is the only route it has: it was briefly
                     stamped on the channel so the inbox could draw a dot per
                     row, and channel data is readable by the jobseeker. The
                     inbox has no dot until there is an employer-scoped way to
                     deliver one. */
                  flagged={assistant.flagged}
                  preview={
                    assistant.replies.length > 0 ? (
                      <AssistantCard.Preview
                        replies={assistant.replies}
                        recommended={assistant.recommended}
                        onWriteOwn={() => composerRef.current?.focus()}
                        onExpand={() => toggleAssistant(true, 'read_more')}
                      />
                    ) : undefined
                  }
                  account={
                    /* What the shut card shows. While a reading is in flight the
                       waiting line stands in for the account — see
                       AssistantCard.Reading. */
                    assistant.reading ? (
                      <AssistantCard.Reading />
                    ) : (
                      /* Told the card's own expand: "Read more" on the cut
                         account is the same press as the chevron. */
                      <AssistantCard.Intro
                        text={assistant.intro}
                        expanded={assistantOpen}
                        onExpand={() => toggleAssistant(true, 'read_more')}
                      />
                    )
                  }
                >
                  {/* One list, one mark. Every move the conversation allows,
                      every time, with the assistant's choice marked — and no
                      mark at all on a reading that named nothing or one that
                      failed, which leaves exactly the command surface that
                      shipped before the assistant existed. */}
                  <AssistantCard.Moves
                    jobBounded={jobBounded}
                    status={status}
                    suggest={assistant.suggest}
                    replies={assistant.replies}
                    recommended={assistant.recommended}
                    translation={trackingTranslation(
                      assistant.translation,
                      assistant.recommended,
                      'chat',
                    )}
                    onTake={takeAssistantAction}
                    onPick={(reply, index) => {
                      assistant.select('reply', String(index));
                      trackAssistantTaken({
                        draftIndex: index,
                        recommended: isRecommendedDraft(
                          assistant.recommended,
                          index,
                          assistant.replies.length,
                        ),
                        by: 'press',
                        surface: 'chat',
                      });
                      composerRef.current?.insert(reply);
                    }}
                    onWriteOwn={() => composerRef.current?.focus()}
                  />
                </AssistantCard>
              ) : (
                /*
                 * The same card, unassisted: "Quick Actions", without the bot
                 * mark or the Beta badge, because nothing has read anything and
                 * so the card claims nothing. One card in two namings rather
                 * than two cards — the employer's controls do not move because
                 * a model is or is not behind them.
                 *
                 * Two ways to land here: the switch says the assistant is off
                 * for this employer, or it is on and had nothing to show — a
                 * reading that failed, or one that came back empty. The naming
                 * is what makes the second honest. A card that says "Assistant"
                 * over four stage rows and no reasoning has promised something
                 * it did not deliver; the same rows under "Quick Actions" have
                 * promised exactly what they are.
                 *
                 * No account, so this one lands as the bare header it always
                 * was: there is nothing it could say without a reading behind it.
                 */
                <AssistantCard
                  expanded={assistantOpen}
                  onExpandedChange={openAssistant}
                  assisted={false}
                >
                  <AssistantCard.Moves
                    jobBounded={jobBounded}
                    status={status}
                    // Static openers, not a model's reading — nothing to mark,
                    // on a row or on a draft.
                    suggest={null}
                    replies={quickReplies.replies}
                    recommended={null}
                    repliesLoading={quickReplies.loading}
                    onTake={takeAssistantAction}
                    onPick={(reply) => {
                      emitToHost('quick-reply-click', {
                        message: reply,
                        source: quickReplies.source,
                      });
                      // `reply_source`, not `source`: the BFF stamps a top-level
                      // `source: 'chat_emp'` that the backend copies over any event
                      // property of the same name, which would erase ai-vs-static.
                      track('quick_reply_used', { reply_source: quickReplies.source });
                      // Records WHICH suggestion was used, against the upstream audit
                      // row. PostHog alone cannot answer that — it only sees that a
                      // quick reply was used. No-op for the static openers.
                      quickReplies.select(reply);
                      composerRef.current?.insert(reply);
                    }}
                    onWriteOwn={() => composerRef.current?.focus()}
                    assisted={false}
                  />
                </AssistantCard>
              )
            ) : undefined
          }
        />
        <ConversationComposer
          ref={composerRef}
          convo={convo}
          formLibrary={formLibrary}
          conversationId={selected.channel.id}
          broadcast={broadcast}
          onManageForms={() => pickSection('forms')}
          onSendError={setWriteError}
          onSend={draft.submit}
          onUpload={convo.uploadAttachment}
          voice={{ surface: 'chat', channelId: cidParts(selected.channel.id).channelId }}
          quoted={draft.staged}
          onCancelQuote={draft.cancel}
          notice={draft.notice}
          disabled={terminal}
          placeholder={terminal ? 'This chat has ended' : undefined}
          onTemplates={() => {
            emitToHost('template-click', {});
            setTemplatesOpen(true);
          }}
          rewardCoins={reward.eligible && !terminal ? reward.coins : undefined}
        />
        {ending && (
          <EndChatDialog
            // Keyed on the chat it was opened for, so the seeded closing message
            // can never outlive the applicant it names.
            key={ending.channelId}
            variant={ending.variant}
            counterpartName={selected.counterpart.name}
            onCancel={() => {
              // The employer backed out of the seeded closing message.
              track('end_chat_draft_cancelled', {
                channel_id: cidParts(ending.channelId).channelId,
              });
              setEnding(null);
            }}
            onConfirm={confirmEnd}
          />
        )}
        {report && (
          <ReportDialog
            cid={selected.channel.id}
            message={report.message}
            counterpartName={selected.counterpart.name}
            onClose={() => setReport(null)}
          />
        )}
        {templatesOpen && (
          <TemplatePanel
            templates={templates.templates}
            loading={templates.loading}
            error={templates.error}
            canCreateMore={templates.canCreateMore}
            onFetch={templates.fetch}
            context={{
              candidateName: selected.counterpart.name,
              jobTitle: selected.channel.jobMeta?.jobTitle,
            }}
            onSendJob={broadcast ? undefined : convo.sendJob}
            onUse={(t, text) => {
              emitToHost('template-use', { id: t.id, name: t.name, template: t.template });
              track('template_used', { template_id: t.id });
              composerRef.current?.insert(text);
            }}
            onCreate={templates.create}
            onUpdate={templates.update}
            onDelete={templates.remove}
            onClose={() => setTemplatesOpen(false)}
          />
        )}
      </div>
    );
  }
}
